import { all, call, put, takeEvery } from '@redux-saga/core/effects';
import axios from 'axios';

import {
  requestDownloadDocument,
  requestUploadDocument,
  requestUploadDocuments,
  requestCancelVideoUpload
} from './document.actions';
import {
  getDownloadDocumentRequest,
  getUploadChunkRequest,
  getUploadDocumentRequest,
  getCancelVideoUploadRequest
} from './document.endpoints';
import {
  DOWNLOADING_DOCUMENT,
  UPLOADING_DOCUMENT,
  UPLOADING_DOCUMENTS,
  CANCELLING_VIDEO_UPLOAD
} from './document.constants';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';

import { ERROR } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { isVideo } from '../../utils/util';

const CHUNK_SIZE = 1024 * 1024 * 5;

export function* performDownloadDocumentRequest({ payload }) {
  const { externalReference, showSpinner = true, onSuccess, arrayBuffer = true } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner(DOWNLOADING_DOCUMENT));
    }

    let responseType;
    if (arrayBuffer) {
      responseType = 'arraybuffer';
    }

    const [endpoint, options] = getDownloadDocumentRequest(externalReference, responseType);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, data);
    }

    if (showSpinner) {
      yield put(removeSpinner(DOWNLOADING_DOCUMENT));
    }
  } catch (error) {
    const message = getErrorMessage(error);

    if (showSpinner) {
      yield put(removeSpinner(DOWNLOADING_DOCUMENT));
    }

    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForDownloadDocumentRequest() {
  yield takeEvery(requestDownloadDocument.type, performDownloadDocumentRequest);
}

export function* performUploadDocumentRequest({ payload }) {
  const { document, onSuccess } = payload;

  try {
    yield put(addSpinner(UPLOADING_DOCUMENT));

    const [endpoint, options] = getUploadDocumentRequest(document, {
      'Content-Type': 'application/x-binary',
      'Content-Length': document.size,
      documentName: document.name
    });

    const { headers } = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, headers.documentexternalreference);
    }

    yield put(removeSpinner(UPLOADING_DOCUMENT));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPLOADING_DOCUMENT));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUploadDocumentRequest() {
  yield takeEvery(requestUploadDocument.type, performUploadDocumentRequest);
}

export function* performUploadDocumentsRequest({ payload }) {
  const { documents, onSuccess } = payload;
  const uploadedDocuments = [];

  try {
    yield put(addSpinner(UPLOADING_DOCUMENTS));

    for (const document of documents) {
      if (isVideo(document.name)) {
        const totalChunks = Math.ceil(document.size / CHUNK_SIZE);
        let currentChunk = 0;

        for (let i = 0; i < totalChunks; i++) {
          const start = i * CHUNK_SIZE;
          const end = Math.min(start + CHUNK_SIZE, document.size);
          const chunk = document.slice(start, end);

          let retries = 0;
          let chunkUploaded = false;

          while (retries < 3 && !chunkUploaded) {
            try {
              const formData = new FormData();
              formData.append('chunk', chunk, `${document.name}.chunk${currentChunk}`);
              formData.append('fileName', document.name);
              formData.append('chunkIndex', currentChunk);
              formData.append('totalChunks', totalChunks);

              const [endpoint, options] = getUploadChunkRequest(formData, {
                'Content-Type': 'multipart/form-data; boundary=!@#'
              });

              const { data } = yield call(axios, endpoint, options);

              if (data.objectKey) {
                uploadedDocuments.push({
                  name: document.name,
                  link: data.objectKey,
                  documentType: 'video'
                });
              }

              currentChunk++;
              chunkUploaded = true;
            } catch (error) {
              retries++;
            }
          }

          if (!chunkUploaded) {
            yield put(requestCancelVideoUpload({ fileName: document.name }));
            throw new Error('Failed to upload video');
          }
        }
      } else {
        const [endpoint, options] = getUploadDocumentRequest(document, {
          'Content-Type': 'application/x-binary',
          'Content-Length': document.size,
          documentName: document.name
        });

        const { headers } = yield call(axios, endpoint, options);

        uploadedDocuments.push({
          name: document.name,
          documentExternalReference: headers.documentexternalreference
        });
      }
    }

    if (onSuccess) {
      yield call(onSuccess, uploadedDocuments);
    }

    yield put(removeSpinner(UPLOADING_DOCUMENTS));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(removeSpinner(UPLOADING_DOCUMENTS));
    yield put(addSystemNotice({ type: ERROR, message }));
  }
}

export function* watchForUploadDocumentsRequest() {
  yield takeEvery(requestUploadDocuments.type, performUploadDocumentsRequest);
}

export function* performCancelVideoUploadRequest({ payload }) {
  try {
    yield put(addSpinner(CANCELLING_VIDEO_UPLOAD));

    const [endpoint, options] = getCancelVideoUploadRequest({
      fileName: payload.fileName
    });

    yield call(axios, endpoint, options);
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CANCELLING_VIDEO_UPLOAD));
  }
}

export function* watchForCancelVideoUploadRequest() {
  yield takeEvery(requestCancelVideoUpload.type, performCancelVideoUploadRequest);
}

export default function* documentSaga() {
  yield all([
    watchForDownloadDocumentRequest(),
    watchForUploadDocumentRequest(),
    watchForUploadDocumentsRequest(),
    watchForCancelVideoUploadRequest()
  ]);
}
